import { createBrowserRouter, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef, type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { setGlobalNavigator } from '@shared/lib/navigation';
import { AuthShell } from './shell/AuthShell';
import { TabShell } from './shell/TabShell';
import { MorePage } from './shell/MorePage';
import { RouteBannerFrame, type ResidentBannerModuleKey } from '@shared/ui/banners/RouteBannerFrame';
import { useAuthStore } from '@shared/stores/authStore';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { ScreenLoader } from '@shared/ui/feedback/ScreenLoader';
import {
  fetchMyOnboardingGate,
  isAwaitingOnboardingApproval,
  onboardingRouteForGate,
} from '@features/join/api';

/**
 * Screens are named exports, so every split point has to re-point `default` at the named
 * component. One helper instead of ~80 hand-written `.then(m => ({ default: m.X }))`.
 */
function lazyScreen(load: () => Promise<unknown>, name: string): ComponentType {
  return lazy(async () => ({ default: (await load() as Record<string, ComponentType>)[name]! }));
}

/* Eager: the screens the very first paint needs (landing, sign-in, and the dashboard behind
   them). Everything below is a separate chunk, fetched when its route is first visited. */
import { Welcome } from '@features/welcome/Welcome';
import { Login } from '@features/auth/Login';
import { Home } from '@features/dashboard/Home';

const WelcomeTour = lazyScreen(() => import('@features/welcome/WelcomeTour'), 'WelcomeTour');
const SelectProperty = lazyScreen(() => import('@features/welcome/SelectProperty'), 'SelectProperty');
const Signup = lazyScreen(() => import('@features/auth/Signup'), 'Signup');
const ForgotPassword = lazyScreen(() => import('@features/auth/ForgotPassword'), 'ForgotPassword');
const ResetPassword = lazyScreen(() => import('@features/auth/ResetPassword'), 'ResetPassword');
const Onboarding = lazyScreen(() => import('@features/onboarding/Onboarding'), 'Onboarding');
const OnboardingPending = lazyScreen(() => import('@features/onboarding/OnboardingPending'), 'OnboardingPending');
const SurveyRunner = lazyScreen(() => import('@features/surveys/SurveyRunner'), 'SurveyRunner');
const PublicAccessInviteAccept = lazyScreen(() => import('@features/access-invites/public/PublicAccessInviteAccept'), 'PublicAccessInviteAccept');
const PublicAccessOnboarding = lazyScreen(() => import('@features/access-invites/public/PublicAccessOnboarding'), 'PublicAccessOnboarding');
const AccessInvitesList = lazyScreen(() => import('@features/access-invites/owner/AccessInvitesList'), 'AccessInvitesList');
const MyVisitors = lazyScreen(() => import('@features/access-invites/owner/MyVisitors'), 'MyVisitors');
const NewAccessInvitePage = lazyScreen(() => import('@features/access-invites/owner/NewAccessInvite'), 'NewAccessInvitePage');
const Profile = lazyScreen(() => import('@features/profile/Profile'), 'Profile');
const PersonalDetails = lazyScreen(() => import('@features/profile/PersonalDetails'), 'PersonalDetails');
const CustomFields = lazyScreen(() => import('@features/profile/CustomFields'), 'CustomFields');
const ProfileDocuments = lazyScreen(() => import('@features/profile/ProfileDocuments'), 'ProfileDocuments');
const Medical = lazyScreen(() => import('@features/profile/Medical'), 'Medical');
const TokenizationCallback = lazyScreen(() => import('@features/billing/TokenizationCallback'), 'TokenizationCallback');
const MyProperties = lazyScreen(() => import('@features/properties/MyProperties'), 'MyProperties');
const PropertyDetail = lazyScreen(() => import('@features/properties/PropertyDetail'), 'PropertyDetail');
const PinPicker = lazyScreen(() => import('@features/properties/PinPicker'), 'PinPicker');
const MyUtilities = lazyScreen(() => import('@features/utilities/MyUtilities'), 'MyUtilities');
const UtilityDetail = lazyScreen(() => import('@features/utilities/UtilityDetail'), 'UtilityDetail');
const PanicSos = lazyScreen(() => import('@features/panic/PanicSos'), 'PanicSos');
const PanicContactForm = lazyScreen(() => import('@features/panic/PanicContactForm'), 'PanicContactForm');
const SupportList = lazyScreen(() => import('@features/support/SupportList'), 'SupportList');
const SupportDetail = lazyScreen(() => import('@features/support/SupportDetail'), 'SupportDetail');
const NewSupportRequest = lazyScreen(() => import('@features/support/NewSupportRequest'), 'NewSupportRequest');
const SupportLiveChat = lazyScreen(() => import('@features/support/SupportLiveChat'), 'SupportLiveChat');
const MemberRequests = lazyScreen(() => import('@features/member-requests/MemberRequests'), 'MemberRequests');
const Documents = lazyScreen(() => import('@features/documents/Documents'), 'Documents');
const NewsStandList = lazyScreen(() => import('@features/news-stand/NewsStandList'), 'NewsStandList');
const NewsStandDetail = lazyScreen(() => import('@features/news-stand/NewsStandDetail'), 'NewsStandDetail');
const GalleryLanding = lazyScreen(() => import('@features/gallery/GalleryLanding'), 'GalleryLanding');
const GalleryAlbum = lazyScreen(() => import('@features/gallery/GalleryAlbum'), 'GalleryAlbum');
const Suppliers = lazyScreen(() => import('@features/suppliers/Suppliers'), 'Suppliers');
const SupplierCategories = lazyScreen(() => import('@features/suppliers/SupplierCategories'), 'SupplierCategories');
const SupplierDetail = lazyScreen(() => import('@features/suppliers/SupplierDetail'), 'SupplierDetail');
const SupplierForm = lazyScreen(() => import('@features/suppliers/SupplierForm'), 'SupplierForm');
const ContactGroups = lazyScreen(() => import('@features/contact-groups/ContactGroups'), 'ContactGroups');
const Classifieds = lazyScreen(() => import('@features/classifieds/Classifieds'), 'Classifieds');
const ClassifiedCategories = lazyScreen(() => import('@features/classifieds/ClassifiedCategories'), 'ClassifiedCategories');
const ClassifiedDetail = lazyScreen(() => import('@features/classifieds/ClassifiedDetail'), 'ClassifiedDetail');
const ClassifiedForm = lazyScreen(() => import('@features/classifieds/ClassifiedForm'), 'ClassifiedForm');
const Events = lazyScreen(() => import('@features/events/Events'), 'Events');
const MapView = lazyScreen(() => import('@features/map/MapView'), 'MapView');
const Management = lazyScreen(() => import('@features/management/Management'), 'Management');
const IframePage = lazyScreen(() => import('@features/iframe/IframePage'), 'IframePage');
const PdfViewerPage = lazyScreen(() => import('@features/pdf-viewer/PdfViewerPage'), 'PdfViewerPage');
const Products = lazyScreen(() => import('@features/products/Products'), 'Products');
const ProductDetail = lazyScreen(() => import('@features/products/ProductDetail'), 'ProductDetail');
const Cart = lazyScreen(() => import('@features/cart/Cart'), 'Cart');
const Checkout = lazyScreen(() => import('@features/checkout/Checkout'), 'Checkout');
const PaymentRedirect = lazyScreen(() => import('@features/checkout/PaymentCallbacks'), 'PaymentRedirect');
const CheckoutSuccess = lazyScreen(() => import('@features/checkout/PaymentCallbacks'), 'CheckoutSuccess');
const PaymentComplete = lazyScreen(() => import('@features/checkout/PaymentComplete'), 'PaymentComplete');
const Orders = lazyScreen(() => import('@features/orders/Orders'), 'Orders');
const OrderDetail = lazyScreen(() => import('@features/orders/OrderDetail'), 'OrderDetail');
const Invoices = lazyScreen(() => import('@features/billing/Invoices'), 'Invoices');
const Settings = lazyScreen(() => import('@features/settings/Settings'), 'Settings');
const Facilities = lazyScreen(() => import('@features/facilities/Facilities'), 'Facilities');
const FacilityDetail = lazyScreen(() => import('@features/facilities/FacilityDetail'), 'FacilityDetail');
const FacilityMyBookings = lazyScreen(() => import('@features/facilities/FacilityMyBookings'), 'FacilityMyBookings');
const SurveysList = lazyScreen(() => import('@features/surveys/SurveysList'), 'SurveysList');
const SurveyDetail = lazyScreen(() => import('@features/surveys/SurveyDetail'), 'SurveyDetail');
const SurveyResponseView = lazyScreen(() => import('@features/surveys/SurveyResponseView'), 'SurveyResponseView');
const MyResponsesList = lazyScreen(() => import('@features/surveys/MyResponsesList'), 'MyResponsesList');
const Pets = lazyScreen(() => import('@features/pets/Pets'), 'Pets');
const PetForm = lazyScreen(() => import('@features/pets/PetForm'), 'PetForm');
const Vehicles = lazyScreen(() => import('@features/vehicles/Vehicles'), 'Vehicles');
const VehicleForm = lazyScreen(() => import('@features/vehicles/VehicleForm'), 'VehicleForm');
const Faq = lazyScreen(() => import('@features/faq/Faq'), 'Faq');
const JoinCommunity = lazyScreen(() => import('@features/join/JoinCommunity'), 'JoinCommunity');
const JoinPending = lazyScreen(() => import('@features/join/JoinPending'), 'JoinPending');
const CommunicationCentre = lazyScreen(() => import('@features/communication/CommunicationCentre'), 'CommunicationCentre');
const CommunicationFeed = lazyScreen(() => import('@features/communication/CommunicationFeed'), 'CommunicationFeed');
const NotificationSettings = lazyScreen(() => import('@features/communication/NotificationSettings'), 'NotificationSettings');
const AdminCommunication = lazyScreen(() => import('@features/communication-admin/AdminCommunication'), 'AdminCommunication');
const InAppMessage = lazyScreen(() => import('@features/communication-admin/InAppMessage'), 'InAppMessage');
const SmsMessage = lazyScreen(() => import('@features/communication-admin/SmsMessage'), 'SmsMessage');

/**
 * Routes mirror `app-routing.module.ts` from the Angular member portal.
 * Auth/onboarding/public flows live above `<TabShell>`; everything under
 * `/app/*` is authenticated and renders inside the tab shell.
 */
/**
 * Root layout for every route. It does two things nothing else can:
 *
 *  - Exposes React Router's client-side navigate to non-React callers (push tap, deep link,
 *    axios interceptors). Without this, navigateTo() falls back to window.location.href,
 *    which a Capacitor local-assets build can't resolve for deep paths (no index.html
 *    rewrite) — so notification and universal-link deep-links did nothing. It sits at the
 *    root rather than inside RequireAuth so signed-out links (invites, password resets)
 *    route too.
 *  - Holds the Suspense boundary for the code-split screens outside the tab shell (the shell
 *    has its own, so its tab bar survives a chunk load).
 */
function RouterBridge(): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setGlobalNavigator((path, options) => navigate(path, options));
  }, [navigate]);

  // Every screen starts at the top. Not keyed by path — that would remount the tab
  // shell on each navigation inside /app; the shell resets its own scroller already.
  useEffect(() => {
    scrollerRef.current?.scrollTo(0, 0);
  }, [pathname]);

  /*
   * The app's scroll container. `html`, `body` and `#root` are all height-bounded with
   * `overflow: hidden` (see styles/globals.css), so a screen that doesn't declare a
   * scroller of its own has anything past the fold clipped with no way to reach it.
   * The tab shell supplies one for `/app/*`; this is the same guarantee for every screen
   * outside it — auth, onboarding, commerce, join — without each having to remember.
   *
   * Inert under the tab shell, which is `h-full` + `overflow-hidden`: it fills this box
   * exactly and scrolls internally, so this scroller never has anything to scroll and
   * the tab bar's hide-on-scroll keeps reading the shell's own container.
   */
  return (
    <div ref={scrollerRef} className="h-full overflow-y-auto overscroll-y-none">
      <Suspense fallback={<ScreenLoader />}>
        <Outlet />
      </Suspense>
    </div>
  );
}

function RequireAuth(): JSX.Element {
  const isAuthed = useAuthStore((s) => !!(s.accessToken || s.refreshToken));
  if (!isAuthed) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RedirectIfAuthed(): JSX.Element {
  const isAuthed = useAuthStore((s) => !!s.accessToken);
  if (isAuthed) return <Navigate to="/app" replace />;
  return <Outlet />;
}

function ResumeFlowFromParam(): JSX.Element {
  const { id, surveyId } = useParams<{ id: string; surveyId: string }>();
  const location = useLocation();
  const isEformRoute = location.pathname.includes('/eforms/');
  const listRoute = isEformRoute ? '/app/eforms' : '/app/surveys';

  if (!id) {
    return <Navigate to={listRoute} replace />;
  }

  const params = new URLSearchParams({
    instance: id,
    context: 'survey',
    kind: isEformRoute ? 'eform' : 'survey',
    title: isEformRoute ? 'eForm' : 'Survey',
    backTo: isEformRoute
      ? (surveyId ? `/app/eforms/${surveyId}` : '/app/eforms')
      : (surveyId ? `/app/surveys/${surveyId}` : '/app/surveys'),
  });

  return <Navigate to={`/survey/run?${params.toString()}`} replace />;
}

function withRouteBanners(moduleKey: ResidentBannerModuleKey, element: JSX.Element): JSX.Element {
  return <RouteBannerFrame moduleKey={moduleKey}>{element}</RouteBannerFrame>;
}

/**
 * Onboarding gate — the React equivalent of the Angular `OnboardingGuard`.
 * Before the member enters the app shell, check `/members/me/onboarding-gate`;
 * if onboarding is still required, redirect into the flow runner.
 *
 * Fails CLOSED: an unanswered gate is not permission to enter. It used to render the app on
 * error, which turned any failed check into a way past onboarding entirely — and asked once
 * per five minutes, so one stale answer let `/app/home` through for the rest of the window.
 */
function OnboardingGate(): JSX.Element {
  const isAuthed = useAuthStore((s) => !!(s.accessToken || s.refreshToken));
  const gateQuery = useQuery({
    queryKey: ['onboarding-gate'],
    queryFn: fetchMyOnboardingGate,
    enabled: isAuthed,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  if (gateQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-paper">
        <span className="h-8 w-8 animate-spin rounded-pill border-2 border-hairline border-t-primary-600" />
      </div>
    );
  }

  if (gateQuery.isError) {
    return (
      <div className="flex h-full items-center justify-center bg-paper">
        <ErrorState
          title="Couldn't check your account."
          description="We can't confirm your onboarding status right now. Check your connection and try again."
          onRetry={() => void gateQuery.refetch()}
        />
      </div>
    );
  }

  // Submitted and waiting on staff: the flow is done, so hold here rather than
  // re-entering a completed runner that can only bounce back to this gate.
  if (gateQuery.data && isAwaitingOnboardingApproval(gateQuery.data)) return <OnboardingPending />;

  const route = gateQuery.data ? onboardingRouteForGate(gateQuery.data) : null;
  if (route) return <Navigate to={route} replace />;

  return <TabShell />;
}

export const router = createBrowserRouter([
  {
    element: <RouterBridge />,
    children: [
    { path: '/', element: <Navigate to="/welcome" replace /> },

    /* ── Auth ─────────────────────────────────────────────────────── */
    {
      element: <RedirectIfAuthed />,
      children: [
        /* Welcome + primary auth screens are full-bleed / self-contained. */
        { path: '/welcome', element: <Welcome /> },
        { path: '/login', element: <Login /> },
        { path: '/signup', element: <Signup /> },
        { path: '/forgot-password', element: <ForgotPassword /> },
        { path: '/reset-password', element: <AuthShell><ResetPassword /></AuthShell> },
      ],
    },

    /* Onboarding — runs in auth shell; runner handles unauth case */
    { path: '/onboarding', element: <AuthShell><Onboarding /></AuthShell> },
    /* Policy/rules PDFs referenced by a flow step. Sits beside the runner rather than
       under /app so the onboarding gate can't bounce a member out of the document.
       No AuthShell — the viewer is full-bleed and paints its own header, so the
       shell's gutter would only shrink the document. */
    { path: '/onboarding/document/view', element: <PdfViewerPage /> },
    { path: '/onboarding/:id', element: <AuthShell><Onboarding /></AuthShell> },

    /* Survey / eForm runner — dedicated runner (shared flow engine, survey variant) */
    { path: '/survey/run', element: <AuthShell><SurveyRunner /></AuthShell> },

    /* Public access invites — no auth required */
    { path: '/access/invite/:token', element: <AuthShell><PublicAccessInviteAccept /></AuthShell> },
    { path: '/access/onboarding/:accessInviteId/:accessToken', element: <AuthShell><PublicAccessOnboarding /></AuthShell> },

    /* Public commerce/community aliases kept for Angular deep-link parity */
    { path: '/events', element: withRouteBanners('events', <Events />) },
    { path: '/products', element: <Navigate to="/products/search" replace /> },
    { path: '/products/search', element: withRouteBanners('products', <Products />) },
    { path: '/products/:id', element: withRouteBanners('products', <ProductDetail />) },
    { path: '/cart', element: withRouteBanners('cart', <Cart />) },
    { path: '/checkout', element: withRouteBanners('checkout', <Checkout />) },

    /* Payment + tokenization callbacks */
    { path: '/tokenization/netcash-callback', element: <TokenizationCallback /> },
    { path: '/checkout/paynow/callback', element: <PaymentRedirect /> },
    { path: '/checkout/payment/redirect', element: <PaymentRedirect /> },
    { path: '/checkout/payment', element: <PaymentRedirect /> },
    { path: '/checkout/success', element: <CheckoutSuccess /> },
    // Public post-payment landing for facility bookings (the in-app browser has no session, so the
    // provider's success/cancel redirect must land somewhere auth-free rather than bouncing to login).
    { path: '/payment/complete', element: <PaymentComplete /> },

    /* ── Authenticated tab shell ──────────────────────────────────── */
    {
      element: <RequireAuth />,
      children: [
        /* First-run flow after account creation — tour → pick residence. */
        { path: '/tour', element: <WelcomeTour /> },
        { path: '/select-property', element: <SelectProperty /> },
        /* Join another community (SSO root scope + build-config gated). */
        { path: '/join', element: <JoinCommunity /> },
        /* A community the member has asked to join and is still waiting on. */
        { path: '/join/pending/:tenantId', element: <JoinPending /> },
        {
          path: '/app',
          element: <OnboardingGate />,
          children: [
            { index: true, element: <Navigate to="home" replace /> },
            { path: 'home', element: <Home /> },
            { path: 'dashboard', element: <Home /> },
            { path: 'more', element: withRouteBanners('more', <MorePage />) },

            /* Book — facilities & bookings */
            { path: 'facilities', element: withRouteBanners('facilities', <Facilities />) },
            { path: 'facilities/my-bookings', element: withRouteBanners('facilities-bookings', <FacilityMyBookings />) },
            { path: 'facilities/:facilityId', element: withRouteBanners('facilities', <FacilityDetail />) },
            {
              path: 'facilities/:facilityId/rule-documents/:ruleDocumentId/view',
              element: withRouteBanners('facilities', <PdfViewerPage />),
              handle: { hideTabBar: true },
            },

            /* Community */
            { path: 'events', element: withRouteBanners('events', <Events />) },
            { path: 'classifieds', element: withRouteBanners('classifieds', <Classifieds />) },
            { path: 'classifieds/categories', element: withRouteBanners('classifieds', <ClassifiedCategories />) },
            { path: 'classifieds/new', element: withRouteBanners('classifieds', <ClassifiedForm />), handle: { hideTabBar: true } },
            { path: 'classifieds/:id', element: withRouteBanners('classifieds', <ClassifiedDetail />) },
            { path: 'classifieds/:id/edit', element: withRouteBanners('classifieds', <ClassifiedForm />), handle: { hideTabBar: true } },
            { path: 'gallery', element: withRouteBanners('gallery', <GalleryLanding />) },
            { path: 'gallery/:id', element: withRouteBanners('gallery', <GalleryAlbum />) },
            { path: 'contact-groups', element: withRouteBanners('contact-groups', <ContactGroups />) },
            { path: 'suppliers', element: withRouteBanners('suppliers', <Suppliers />) },
            { path: 'suppliers/categories', element: withRouteBanners('suppliers', <SupplierCategories />) },
            { path: 'suppliers/new', element: withRouteBanners('suppliers', <SupplierForm />), handle: { hideTabBar: true } },
            { path: 'suppliers/:id', element: withRouteBanners('suppliers', <SupplierDetail />) },
            { path: 'suppliers/:id/edit', element: withRouteBanners('suppliers', <SupplierForm />), handle: { hideTabBar: true } },

            /* Communication Centre */
            { path: 'communication', element: <CommunicationCentre /> },
            { path: 'communication/feed', element: <CommunicationFeed />, handle: { hideTabBar: true } },
            { path: 'communication/settings', element: <NotificationSettings />, handle: { hideTabBar: true } },
            { path: 'news-stands', element: withRouteBanners('newsstand', <NewsStandList />) },
            { path: 'news-stands/:newsstandId', element: withRouteBanners('newsstand', <NewsStandDetail />) },
            {
              path: 'news-stands/:newsstandId/articles/:articleId/view',
              element: withRouteBanners('newsstand', <PdfViewerPage />),
              handle: { hideTabBar: true },
            },
            { path: 'surveys', element: withRouteBanners('surveys', <SurveysList />) },
            { path: 'surveys/:id', element: withRouteBanners('surveys', <SurveyDetail />) },
            { path: 'surveys/:surveyId/fill/:id', element: <ResumeFlowFromParam /> },
            { path: 'surveys/:surveyId/responses', element: withRouteBanners('surveys', <MyResponsesList />) },
            { path: 'surveys/:surveyId/response/:responseId', element: withRouteBanners('surveys', <SurveyResponseView />) },
            { path: 'eforms/:id', element: withRouteBanners('eforms', <SurveyDetail />) },
            { path: 'eforms/:surveyId/fill/:id', element: <ResumeFlowFromParam /> },
            { path: 'pets', element: withRouteBanners('pets', <Pets />) },
            { path: 'pets/new', element: withRouteBanners('pets', <PetForm />), handle: { hideTabBar: true } },
            { path: 'pets/:petId/edit', element: withRouteBanners('pets', <PetForm />), handle: { hideTabBar: true } },
            { path: 'vehicles', element: withRouteBanners('vehicles', <Vehicles />) },
            { path: 'vehicles/new', element: withRouteBanners('vehicles', <VehicleForm />), handle: { hideTabBar: true } },
            { path: 'vehicles/:vehicleId/edit', element: withRouteBanners('vehicles', <VehicleForm />), handle: { hideTabBar: true } },

            /* Estate */
            { path: 'map', element: withRouteBanners('map', <MapView />) },
            { path: 'management', element: withRouteBanners('management', <Management />) },
            { path: 'documents', element: withRouteBanners('documents', <Documents />) },
            { path: 'documents/:documentId/view', element: withRouteBanners('documents', <PdfViewerPage />), handle: { hideTabBar: true } },
            { path: 'iframe', element: withRouteBanners('iframe', <IframePage />) },

            /* Support */
            { path: 'support/requests', element: withRouteBanners('support', <SupportList />) },
            { path: 'support/requests/new', element: withRouteBanners('support', <NewSupportRequest />), handle: { hideTabBar: true } },
            { path: 'support/requests/:id', element: withRouteBanners('support', <SupportDetail />) },
            { path: 'support/chat', element: withRouteBanners('support', <SupportLiveChat />) },
            { path: 'member-requests', element: <MemberRequests /> },

            /* Admin: broadcast console (communication:bulk_communications:publish) */
            { path: 'admin/communication', element: <AdminCommunication /> },
            { path: 'admin/communication/in-app', element: <InAppMessage />, handle: { hideTabBar: true } },
            { path: 'admin/communication/sms', element: <SmsMessage />, handle: { hideTabBar: true } },

            /* Commerce */
            { path: 'products', element: withRouteBanners('products', <Products />) },
            { path: 'products/search', element: withRouteBanners('products', <Products />) },
            { path: 'products/:id', element: withRouteBanners('products', <ProductDetail />) },
            { path: 'cart', element: withRouteBanners('cart', <Cart />) },
            { path: 'checkout', element: withRouteBanners('checkout', <Checkout />) },
            { path: 'orders', element: withRouteBanners('orders', <Orders />) },
            { path: 'orders/:orderId', element: withRouteBanners('orders', <OrderDetail />) },
            { path: 'invoices', element: withRouteBanners('billing', <Invoices />) },
            {
              path: 'invoices/:transactionId/view',
              element: withRouteBanners('billing', <PdfViewerPage />),
              handle: { hideTabBar: true },
            },

            /* Account */
            { path: 'profile', element: withRouteBanners('profile', <Profile />) },
            { path: 'profile/personal', element: withRouteBanners('profile', <PersonalDetails />), handle: { hideTabBar: true } },
            { path: 'profile/custom', element: withRouteBanners('profile', <CustomFields />), handle: { hideTabBar: true } },
            { path: 'profile/documents', element: withRouteBanners('profile', <ProfileDocuments />), handle: { hideTabBar: true } },
            { path: 'profile/medical', element: withRouteBanners('profile', <Medical />), handle: { hideTabBar: true } },
            { path: 'my-properties', element: withRouteBanners('properties', <MyProperties />) },
            {
              path: 'my-properties/:locationId',
              element: withRouteBanners('properties', <PropertyDetail />),
              handle: { hideTabBar: true },
            },
            {
              path: 'my-properties/:locationId/location',
              element: withRouteBanners('properties', <PinPicker />),
              handle: { hideTabBar: true },
            },
            { path: 'utilities', element: withRouteBanners('utilities', <MyUtilities />) },
            { path: 'utilities/:linkId', element: withRouteBanners('utilities', <UtilityDetail />) },
            { path: 'settings', element: withRouteBanners('settings', <Settings />) },
            { path: 'faq', element: withRouteBanners('faq', <Faq />) },

            /* Safety */
            { path: 'panic', element: withRouteBanners('panic', <PanicSos />) },
            { path: 'sos', element: withRouteBanners('panic', <PanicSos />) },
            { path: 'panic/contacts', element: <Navigate to="/app/panic?tab=contacts" replace /> },
            { path: 'panic/contacts/new', element: withRouteBanners('panic', <PanicContactForm />), handle: { hideTabBar: true } },
            { path: 'panic/contacts/:contactId/edit', element: withRouteBanners('panic', <PanicContactForm />), handle: { hideTabBar: true } },

            /* Access invites */
            { path: 'access-invites', element: withRouteBanners('access-management', <AccessInvitesList />) },
            { path: 'access-invites/new', element: withRouteBanners('access-management', <NewAccessInvitePage />) },
            { path: 'my-visitors', element: withRouteBanners('access-management', <MyVisitors />) },
            { path: 'access-invites/:id/selfie', element: <Navigate to="/app/access-invites" replace /> },
          ],
        },
      ],
    },

    { path: '*', element: <Navigate to="/welcome" replace /> },
    ],
  },
]);
